// The bots' faces (AI_MANAGERS.md §5.4, scenario B7, B-464): each character's
// face as a `Face`, the shape a person's face has (faces.ts, B-488), so one
// renderer draws both and a person can start from Grandma Dot.
//
// A FACE IS A BADGE a seat a bot holds wears (`TeamBadge` kind `bot`, its id
// the character's), so wherever a team is drawn the face is, with the tag a
// client draws on a bot's mark: that is how a bot is marked as a bot
// everywhere (§5). A character with no entry here is drawn with its initials.

import {
  DEFAULT_FACE_COLORS,
  FACE_MARKS_MAX,
  NO_ADJUST,
  chooseFacePart,
  darken,
  faceSeasonsOn,
  type Face,
  type FaceColors,
  type FaceMood,
  type FacePartKey,
  type FaceSeasonId,
} from './faces';

/** What a face is doing (§5.1). The name the bots' faces had first. */
export type BotFaceState = FaceMood;

/** A character's face from the parts it wears, its brows a shade of its hair as they were drawn. */
function cast(
  parts: Omit<Face, 'colors' | 'adjust' | 'marks'> & { marks?: Face['marks'] },
  colors: Partial<FaceColors> & Pick<FaceColors, 'skin' | 'hair'>,
): Face {
  return {
    ...parts,
    marks: parts.marks ?? [],
    colors: {
      ...DEFAULT_FACE_COLORS,
      brows: parts.brows === 'bushy' ? colors.hair : darken(colors.hair),
      facial: colors.hair,
      ...colors,
    },
    adjust: NO_ADJUST,
  };
}

const INK = '#2b1d16';

/** Every character's face, by the id a seat stores (`presetId`). */
export const BOT_FACES: Readonly<Record<string, Face>> = {
  'captain-hype': cast(
    {
      head: 'round',
      ears: 'round',
      hair: 'spiky',
      eyes: 'wide',
      brows: 'thick',
      nose: 'none',
      mouth: 'grin',
      facial: 'none',
      headwear: 'shades-up',
      eyewear: 'none',
      neck: 'none',
    },
    { skin: '#f1c27d', hair: '#ffcf33', eyes: INK, hat: '#1d1d1d' },
  ),
  'chaos-gremlin': cast(
    {
      head: 'round',
      ears: 'pointy',
      hair: 'messy',
      eyes: 'wide',
      brows: 'thin',
      nose: 'none',
      mouth: 'teeth',
      facial: 'none',
      headwear: 'horns',
      eyewear: 'none',
      neck: 'none',
    },
    { skin: '#7cc350', hair: '#3f6d22', eyes: INK, hat: '#f4e9d0' },
  ),
  'coach-bot': cast(
    {
      head: 'robot',
      ears: 'bolts',
      hair: 'none',
      eyes: 'leds',
      brows: 'none',
      nose: 'none',
      mouth: 'smile',
      facial: 'none',
      headwear: 'antenna',
      eyewear: 'none',
      neck: 'whistle',
    },
    {
      skin: '#b9c6d8',
      hair: '#b9c6d8',
      eyes: '#3ee0ff',
      hat: '#7d8aa0',
      hatTrim: '#ff5a5a',
      neck: '#d32f2f',
      neckTrim: '#cfd4da',
    },
  ),
  'grandma-dot': cast(
    {
      head: 'round',
      ears: 'round',
      hair: 'bun',
      eyes: 'round',
      brows: 'thin',
      nose: 'none',
      mouth: 'smile',
      facial: 'none',
      headwear: 'none',
      eyewear: 'round',
      neck: 'pearls',
    },
    {
      skin: '#f5d3b8',
      hair: '#d6d6d6',
      eyes: INK,
      frames: INK,
      lenses: '#ffffff',
      neck: '#fbf7ee',
    },
  ),
  'lucky-lou': cast(
    {
      head: 'round',
      ears: 'round',
      hair: 'short',
      eyes: 'sleepy',
      brows: 'thin',
      nose: 'none',
      mouth: 'smirk',
      facial: 'none',
      headwear: 'cap',
      eyewear: 'none',
      neck: 'none',
    },
    { skin: '#e0ac69', hair: '#6b4423', eyes: INK, hat: '#2e8b57', hatTrim: '#1f6b41' },
  ),
  'midnight-mike': cast(
    {
      head: 'round',
      ears: 'round',
      hair: 'messy',
      eyes: 'sleepy',
      brows: 'thick',
      nose: 'none',
      mouth: 'flat',
      facial: 'none',
      headwear: 'hood',
      eyewear: 'none',
      neck: 'none',
    },
    { skin: '#c68642', hair: '#262626', eyes: INK, hat: '#34436a', hatTrim: '#222b44' },
  ),
  'sunday-sam': cast(
    {
      head: 'round',
      ears: 'round',
      hair: 'curly',
      eyes: 'sleepy',
      brows: 'thin',
      nose: 'none',
      mouth: 'smile',
      facial: 'none',
      headwear: 'sleep-mask',
      eyewear: 'none',
      neck: 'none',
    },
    { skin: '#ffdcb0', hair: '#a0522d', eyes: INK, hat: '#7fb3ff', hatTrim: '#a9cbff' },
  ),
  'the-accountant': cast(
    {
      head: 'square',
      ears: 'round',
      hair: 'side',
      eyes: 'round',
      brows: 'thick',
      nose: 'none',
      mouth: 'flat',
      facial: 'none',
      headwear: 'none',
      eyewear: 'square',
      neck: 'tie',
    },
    {
      skin: '#8d5524',
      hair: '#1b1b1b',
      eyes: INK,
      frames: INK,
      lenses: '#ffffff',
      neck: '#7a1f2b',
      neckTrim: '#f4f4f4',
    },
  ),
  'the-intern': cast(
    {
      head: 'round',
      ears: 'round',
      hair: 'short',
      eyes: 'wide',
      brows: 'thin',
      nose: 'none',
      mouth: 'grin',
      facial: 'none',
      headwear: 'none',
      eyewear: 'none',
      neck: 'lanyard',
    },
    { skin: '#f3c9a8', hair: '#e0b03a', eyes: INK, neck: '#1e88e5', neckTrim: '#ffffff' },
  ),
  'the-professor': cast(
    {
      head: 'round',
      ears: 'round',
      hair: 'bald',
      eyes: 'round',
      brows: 'bushy',
      nose: 'none',
      mouth: 'smirk',
      facial: 'full',
      headwear: 'none',
      eyewear: 'round',
      neck: 'bowtie',
    },
    {
      skin: '#d9a066',
      hair: '#e6e6e6',
      eyes: INK,
      frames: INK,
      lenses: '#ffffff',
      neck: '#c0392b',
    },
  ),
  'uncle-rico': cast(
    {
      head: 'round',
      ears: 'round',
      hair: 'mullet',
      eyes: 'round',
      brows: 'thick',
      nose: 'none',
      mouth: 'smirk',
      facial: 'mustache',
      headwear: 'headband',
      eyewear: 'none',
      neck: 'none',
    },
    { skin: '#e8b48a', hair: '#5a3a1a', eyes: INK, hat: '#e53935' },
  ),
  'waiver-wendy': cast(
    {
      head: 'round',
      ears: 'round',
      hair: 'ponytail',
      eyes: 'round',
      brows: 'thick',
      nose: 'none',
      mouth: 'smile',
      facial: 'none',
      headwear: 'headset',
      eyewear: 'none',
      neck: 'none',
    },
    { skin: '#a86b45', hair: '#3a1d0e', eyes: INK, hat: '#333333' },
  ),
};

/**
 * What a character changes for a season: a few parts, each bringing its own
 * colors as a choice in the maker does, and any colors of its own over them.
 */
export interface Costume {
  readonly parts: Partial<Pick<Face, FacePartKey | 'marks'>>;
  readonly colors?: Partial<FaceColors>;
}

/**
 * The cast dressed up for a season (PLAN-2026-10-24-faces-round-two §2): each
 * character's own costume over its face, worn wherever its face is while the
 * season is on, and the same character the rest of the year. A character
 * with no costume for a season wears its face.
 */
export const BOT_COSTUMES: Readonly<
  Partial<Record<FaceSeasonId, Readonly<Record<string, Costume>>>>
> = {
  halloween: {
    'captain-hype': { parts: { neck: 'cape' } },
    'chaos-gremlin': { parts: { headwear: 'pumpkin' } },
    'coach-bot': { parts: { headwear: 'bat-headband' }, colors: { hat: '#1d1d1d' } },
    'grandma-dot': { parts: { headwear: 'witch-hat' } },
    'lucky-lou': {
      parts: { headwear: 'cat-ears', marks: ['whiskers'] },
      colors: { hat: '#1d1d1d', hatTrim: '#f7b6c8' },
    },
    'midnight-mike': { parts: { mouth: 'fangs' } },
    // Asleep so long the cobwebs have grown.
    'sunday-sam': { parts: { marks: ['web'] } },
    // The Count.
    'the-accountant': { parts: { neck: 'cape', mouth: 'fangs' } },
    'the-intern': { parts: { headwear: 'pumpkin', mouth: 'jagged' } },
    'the-professor': { parts: { headwear: 'wizard' } },
    'uncle-rico': { parts: { headwear: 'horns' }, colors: { hat: '#e53935' } },
    'waiver-wendy': { parts: { headwear: 'witch-hat' }, colors: { hatTrim: '#2e8b57' } },
  },
  thanksgiving: {
    'coach-bot': { parts: { headwear: 'turkey' } },
    'uncle-rico': { parts: { headwear: 'turkey' } },
  },
  winter: {
    'captain-hype': { parts: { headwear: 'santa' } },
    'chaos-gremlin': { parts: { headwear: 'elf' } },
    // Rudolph's nose.
    'coach-bot': { parts: { headwear: 'antlers', nose: 'clown' } },
    'grandma-dot': { parts: { neck: 'sweater' } },
    'lucky-lou': { parts: { headwear: 'santa' } },
    'midnight-mike': { parts: { marks: ['snowflake'] } },
    'sunday-sam': { parts: { neck: 'sweater' } },
    'the-accountant': { parts: { neck: 'sweater' }, colors: { neck: '#2e8b57' } },
    'the-intern': { parts: { headwear: 'antlers' } },
    'the-professor': { parts: { headwear: 'santa' } },
    'uncle-rico': { parts: { headwear: 'santa' } },
    'waiver-wendy': { parts: { headwear: 'elf' } },
  },
  valentines: {
    'grandma-dot': { parts: { headwear: 'heart-boppers' } },
    'waiver-wendy': { parts: { headwear: 'heart-boppers' } },
  },
  'st-patricks': {
    'lucky-lou': { parts: { headwear: 'shamrock-hat', marks: ['shamrock'] } },
  },
};

/**
 * A face in a costume: its parts chosen as the maker chooses them, each
 * bringing its own colors, its extras worn beside the face's own, then the
 * costume's colors over all.
 */
export function dressFace(face: Face, costume: Costume): Face {
  let out = face;
  for (const [key, value] of Object.entries(costume.parts)) {
    if (key !== 'marks') {
      out = chooseFacePart(out, key as FacePartKey, value as string);
      continue;
    }
    for (const mark of value as Face['marks'])
      if (!out.marks.includes(mark) && out.marks.length < FACE_MARKS_MAX)
        out = chooseFacePart(out, 'marks', mark);
  }
  return costume.colors ? { ...out, colors: { ...out.colors, ...costume.colors } } : out;
}

/**
 * A character's face, or null for one with none (drawn with its initials);
 * on a day in a season, in its costume for the season.
 */
export function botFace(presetId: string, on?: Date): Face | null {
  const face = BOT_FACES[presetId];
  if (face === undefined) return null;
  if (on === undefined) return face;
  for (const season of faceSeasonsOn(on)) {
    const costume = BOT_COSTUMES[season]?.[presetId];
    if (costume) return dressFace(face, costume);
  }
  return face;
}
